import { useForm, Resolver } from 'react-hook-form';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import {
  createTaxRateAsync,
  fetchTaxRateDetailsAsync,
  updateTaxRateAsync,
} from './taxSlice';
import { TaxRateForm as TaxRateFormData } from '../../../app/data/models/taxes/taxRate';
import { Box, FormControlLabel, Grid, Paper, Switch } from '@mui/material';
import AppTextInput from '../../../app/components/AppTextInput';
import FormButtons from '../../../app/components/FormButtons';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ObjectSchema } from 'yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useRestaurant } from '../../../app/hooks/useRestaurant';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import ErrorComponent from '../../../app/layout/ErrorComponent';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import { LanguageFormTabs } from '../../../app/components/LanguageFormTabs';
import {
  LanguageCode,
  LocalizedLanguageFormData,
} from '../../../app/data/models/common/localizedLanguage';
import {
  formDataToLocalizedString,
  localizedStringToFormData,
  toLocalizedString,
} from '../../../app/data/models/common/localizedString';
import { FieldErrors } from 'react-hook-form';
import { toast } from 'react-toastify';
import { TFunction } from 'node_modules/i18next/typescript/t';

interface Props {
  restaurantId: string;
  taxRateId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const validationSchema = (
  t: TFunction,
  languageCodes: LanguageCode[]
): ObjectSchema<TaxRateFormData> => {
  // Create a base schema for language fields
  const baseLanguageSchema = languageCodes.reduce(
    (acc, code) => {
      acc[code] = yup.string().required(t('common.validation.required'));
      return acc;
    },
    {} as Record<LanguageCode, yup.StringSchema<string>>
  );

  return yup.object({
    names: yup.object().shape(baseLanguageSchema).required(),
    descriptions: yup.object().shape(
      languageCodes.reduce(
        (acc, code) => {
          acc[code] = yup.string();
          return acc;
        },
        {} as Record<LanguageCode, yup.StringSchema<string>>
      )
    ),
    rate: yup
      .number()
      .typeError(t('validation.number-required'))
      .required(t('common.validation.required'))
      .min(0)
      .max(100),
    isDefault: yup.boolean().required(),
  }) as ObjectSchema<TaxRateFormData>;
};

export default function TaxRateForm({
  restaurantId,
  taxRateId,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { restaurant, isLoading, error, retry } = useRestaurant(restaurantId);
  const {
    taxRate: taxRateDetails,
    taxRateDetailsStatus,
    taxRateCreateStatus,
    taxRateUpdateStatus,
  } = useAppSelector((state) => state.taxes);
  const [activeTab, setActiveTab] = useState(0);
  const defaultValues: TaxRateFormData = useMemo(
    () => ({
      names: (restaurant?.languageCodes || []).reduce(
        (acc, code) => ({ ...acc, [code]: '' }),
        {} as LocalizedLanguageFormData
      ),
      descriptions: (restaurant?.languageCodes || []).reduce(
        (acc, code) => ({ ...acc, [code]: '' }),
        {} as LocalizedLanguageFormData
      ),
      rate: 0,
      isDefault: false,
    }),
    [restaurant?.languageCodes]
  );
  const formInitializedRef = useRef(false);

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<TaxRateFormData>({
      resolver: yupResolver(
        validationSchema(t, restaurant?.languageCodes || [])
      ) as unknown as Resolver<TaxRateFormData>,
      mode: 'onTouched',
      defaultValues,
    });

  const isLoadingForm =
    taxRateCreateStatus === 'loading' || taxRateUpdateStatus === 'loading';
  useEffect(() => {
    if (
      taxRateId &&
      taxRateId !== taxRateDetails?.id &&
      taxRateDetailsStatus !== 'loading'
    ) {
      dispatch(fetchTaxRateDetailsAsync({ restaurantId, taxRateId }));
    }
  }, [
    taxRateId,
    restaurantId,
    dispatch,
    taxRateDetails?.id,
    taxRateDetailsStatus,
  ]);

  useEffect(() => {
    if (taxRateId && taxRateDetails) {
      // Convert the multi-language taxRateDetails to form data
      const formData: TaxRateFormData = {
        names: taxRateDetails.names
          ? localizedStringToFormData(taxRateDetails.names)
          : (restaurant?.languageCodes || []).reduce(
              (acc, code) => ({ ...acc, [code]: '' }),
              {} as LocalizedLanguageFormData
            ),
        descriptions: taxRateDetails.descriptions
          ? localizedStringToFormData(taxRateDetails.descriptions)
          : (restaurant?.languageCodes || []).reduce(
              (acc, code) => ({ ...acc, [code]: '' }),
              {} as LocalizedLanguageFormData
            ),
        rate: taxRateDetails.rate,
        isDefault: taxRateDetails.isDefault,
      };
      reset(formData);
      formInitializedRef.current = false; // Reset for next create mode
    } else if (!taxRateId && !formInitializedRef.current) {
      // Reset to default values when creating new tax rate (only once)
      reset(defaultValues);
      formInitializedRef.current = true;
    }
  }, [taxRateId, taxRateDetails, reset, restaurant?.languageCodes]);

  useEffect(() => {
    // Reset form initialization state when switching modes
    formInitializedRef.current = false;
  }, [taxRateId]);

  const onError = (errors: FieldErrors<TaxRateFormData>) => {
    if (!restaurant?.languageCodes?.length) return;

    // Check names field errors
    if (errors.names) {
      const errorLanguageIndex = restaurant.languageCodes.findIndex(
        (code) => errors.names && errors.names[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
        return;
      }
    }

    // Check descriptions field errors
    if (errors.descriptions) {
      const errorLanguageIndex = restaurant.languageCodes.findIndex(
        (code) => errors.descriptions && errors.descriptions[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
      }
    }
  };

  async function submitForm(formData: TaxRateFormData) {
    try {
      // Convert form data to API request format
      const data = {
        id: taxRateId,
        names: formDataToLocalizedString(formData.names),
        descriptions: formDataToLocalizedString(formData.descriptions),
        rate: formData.rate,
        isDefault: formData.isDefault,
      };

      const action = taxRateId
        ? updateTaxRateAsync({
            restaurantId,
            taxRateId: taxRateId,
            data,
          })
        : createTaxRateAsync({ restaurantId, data });

      await dispatch(action).unwrap();
      toast.success(
        taxRateId
          ? t('taxes.form.update-success')
          : t('taxes.form.create-success')
      );
      onSuccess();
    } catch (error) {
      toast.error(t('taxes.form.save-error'));
      console.error('Failed to save tax rate:', error);
    }
  }

  if (isLoading) return <LoadingComponent />;
  if (error) return <ErrorComponent onPrimaryAction={retry} />;
  if (!restaurant) return null;

  if (taxRateId && taxRateDetailsStatus === 'loading') {
    return <LoadingComponent message={t('common.loading')} />;
  }

  return (
    <>
      <ListTitleBox
        title={
          taxRateId ? t('taxes.form.edit-title') : t('taxes.form.create-title')
        }
        onBackClick={onClose}
      />
      <Box component={Paper} sx={{ p: 2, width: '100%', margin: 'auto' }}>
        <Box
          component="form"
          onSubmit={handleSubmit(submitForm, onError)}
          noValidate
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <LanguageFormTabs
                languageCodes={restaurant.languageCodes}
                control={control}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                fields={[
                  {
                    name: 'names',
                    label: t('taxes.form.fields.name'),
                  },
                  {
                    name: 'descriptions',
                    label: t('taxes.form.fields.description'),
                    multiline: true,
                    rows: 3,
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <AppTextInput
                control={control}
                name="rate"
                label={t('taxes.form.fields.rate')}
                type="number"
                InputProps={{
                  inputProps: { min: 0, max: 100, step: 0.01 },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('isDefault')}
                    onChange={(e) => setValue('isDefault', e.target.checked)}
                  />
                }
                label={t('taxes.form.fields.is-default')}
              />
            </Grid>
          </Grid>
          <FormButtons
            cancelEdit={onClose}
            loading={isLoadingForm}
            cancelText={t('common.cancel')}
            submitText={taxRateId ? t('common.update') : t('common.create')}
          />
        </Box>
      </Box>
    </>
  );
}
